import React, { useState, useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import CodeRequestCard from '../components/CodeRequestCard';
import CreateCodeRequest from '../components/CreateCodeRequest';
import '../index.css';

const GameCodeRequests = () => {
  const { gameName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [codeRequests, setCodeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [filter, setFilter] = useState({
    platform: location.state?.platform || 'all',
    status: 'active'
  });

  const decodedGameName = decodeURIComponent(gameName);

  const fetchCodeRequests = async () => {
    try {
      setLoading(true);
      const params = {
        type: 'request',
        status: filter.status,
        page: currentPage,
        limit: 20
      };

      if (filter.platform !== 'all') {
        params.platform = filter.platform;
      }

      const response = await axios.get(`/api/code-requests/game/${encodeURIComponent(decodedGameName)}`, { params });
      setCodeRequests(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching code requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodeRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, currentPage]);

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchCodeRequests();
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      steam: '🎮',
      epic: '🏪',
      gog: '🕹️',
      xbox: '🎯',
      playstation: '🎲',
      nintendo: '🎴',
      origin: '🌟',
      uplay: '🏰',
      other: '📦'
    };
    return icons[platform] || '📦';
  };

  const getPlatformName = (platform) => {
    const names = {
      steam: 'Steam',
      epic: 'Epic Games',
      gog: 'GOG',
      xbox: 'Xbox',
      playstation: 'PlayStation',
      nintendo: 'Nintendo',
      origin: 'Origin',
      uplay: 'Uplay',
      other: 'Other'
    };
    return names[platform] || platform;
  };

  return (
    <div className="code-requests-page">
      <div className="game-requests-header">
        <button onClick={() => navigate('/code-requests')} className="back-button">
          ← Back to All Requests
        </button>
        
        <div className="game-header-content">
          <h1 className="code-requests-title">
            🎮 {decodedGameName}
          </h1>
          <p className="code-requests-subtitle">
            {pagination.total || 0} {pagination.total === 1 ? 'person is' : 'people are'} requesting this game
          </p>
        </div>
        
        {isAuthenticated && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="create-btn request-btn"
          >
            + Request This Game
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Platform:</label>
          <select
            value={filter.platform}
            onChange={(e) => setFilter({ ...filter, platform: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Platforms</option>
            <option value="steam">Steam</option>
            <option value="epic">Epic Games</option>
            <option value="gog">GOG</option>
            <option value="xbox">Xbox</option>
            <option value="playstation">PlayStation</option>
            <option value="nintendo">Nintendo</option>
            <option value="origin">Origin</option>
            <option value="uplay">Uplay</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="filter-select"
          >
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Info Box */}
      <div className="game-info-box">
        <div className="info-item">
          <span className="info-icon">📊</span>
          <span className="info-text">
            <strong>{pagination.total || 0}</strong> {pagination.total === 1 ? 'request' : 'requests'}
          </span>
        </div>
        {filter.platform !== 'all' && (
          <div className="info-item">
            <span className="info-icon">{getPlatformIcon(filter.platform)}</span>
            <span className="info-text">{getPlatformName(filter.platform)}</span>
          </div>
        )}
      </div>

      {/* Code Requests Grid */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      ) : codeRequests.length === 0 ? (
        <div className="empty-state">
          <p>No requests found for this game</p>
          {isAuthenticated && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="create-btn-empty"
            >
              + Be the First to Request
            </button>
          )}
        </div>
      ) : (
        <div className="code-requests-grid">
          {codeRequests.map((request) => (
            <CodeRequestCard
              key={request._id}
              codeRequest={request}
              onUpdate={fetchCodeRequests}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
            disabled={currentPage === pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateCodeRequest
          type="request"
          defaultGameName={decodedGameName}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};

export default GameCodeRequests;
